import { Typography, Box } from "@mui/material";
import React from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import moment from "moment";
import StatusIndicator from "./StatusIndicator";
import { spFullNameSelector } from "../../../reducers/serviceProvider";
import { Booking } from "../../../interfaces/BookingInterfaces";
import useGetBookedServices from "../../../hooks/useGetBookedServices";
import HtmlTooltip from "../../../components/HtmlTooltip";

interface PastBookingsProps {
  bookings: Booking[];
  setClientId: (id: number) => void;
  setShowClientInfoPanel: (show: boolean) => void;
  setBooking: (booking: Booking) => void;
  handleClick: (e) => void;
  isToday: boolean;
}

const BookingDetail = ({
  setClientId,
  setShowClientInfoPanel,
  setBooking,
  booking,
  handleClick
}) => {
  const { start, serviceProvider, client, status, bookableResource, services = [] } = booking;
  const bookedServices = useGetBookedServices(services);
  const bookedServicesString = (bookedServices || []).map((service) => service.name).join(", ");

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        height: { xs: "auto", sm: "40px" },
        cursor: "pointer",
        borderBottom: "1px solid rgba(90, 96, 127, 0.25)",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 0.5, sm: 0 },
        py: { xs: 1, sm: 0 },
        fontWeight: 500,
        fontSize: "12px",
        lineHeight: "18px",
        "&:hover": { backgroundColor: "#F1FFF3" }
      }}
      onClick={(e) => {
        setBooking(booking);
        handleClick(e);
      }}
    >
      <Box
        sx={{
          flexBasis: "20%",
          textTransform: "uppercase",
          fontFamily: "Noto Sans Display",
          display: "flex",
          gap: 2,
          alignItems: "center"
        }}
      >
        <Typography whiteSpace="pre">{moment(start).format("hh:mm a")}</Typography>
        <StatusIndicator status={status} />
        {booking.remarks && <HtmlTooltip description={booking.remarks} />}
      </Box>
      <Box
        sx={{
          display: "flex",
          maxWidth: "15.5rem",
          flexBasis: "50%",
          gap: "0.5rem"
        }}
      >
        {booking.bookableResourceId && (
          <Typography
            title={booking.bookableResource.name}
            sx={{
              fontWeight: 400,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {booking.bookableResource.name}
          </Typography>
        )}
        <Typography
          title={bookedServicesString}
          sx={{
            fontWeight: 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {bookedServicesString}
        </Typography>
      </Box>
      <Box
        sx={{
          fontWeight: "bold",
          fontSize: "12px",
          lineHeight: "18px",
          cursor: "pointer",
          flexBasis: "33%",
          textAlign: "right",
          textTransform: "capitalize",
          "&:hover": { textDecoration: "underline" }
        }}
        onClick={(e) => {
          e.stopPropagation();
          setClientId(client?.id);
          setShowClientInfoPanel(true);
        }}
      >
        {`${client?.firstName} ${client?.lastName}`}
      </Box>
      {(bookableResource || serviceProvider) && (
        <Typography sx={{ textAlign: "right", flexBasis: "33%", textTransform: "capitalize" }}>
          {serviceProvider ? spFullNameSelector(serviceProvider) : bookableResource.name}
        </Typography>
      )}
    </Box>
  );
};

const PastBookings = ({
  bookings,
  setClientId,
  setShowClientInfoPanel,
  setBooking,
  handleClick,
  isToday
}: PastBookingsProps): React.ReactElement => {
  const oneHourBookings = bookings.filter(
    (booking) => moment().diff(moment(booking.end), "hours", true) < 1
  );
  const [showPastBooking, setShowPastBooking] = React.useState(false);

  const bookingsToShow = showPastBooking || !isToday ? bookings : oneHourBookings;

  return (
    <Box>
      {isToday && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#009654",
            cursor: "pointer"
          }}
          onClick={() => setShowPastBooking((prev) => !prev)}
        >
          <Typography variant="h5" sx={{ fontWeight: 500, fontSize: "12px", lineHeight: "18px" }}>
            {bookings.length} Past Bookings
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              transform: showPastBooking ? "rotate(180deg)" : "rotate(360deg)",
              transition: "all 0.5s"
            }}
          />
        </Box>
      )}
      <Box sx={{ marginTop: "20px", fontWeight: 500, fontSize: "12px", lineHeight: "18px" }}>
        {bookingsToShow?.map((booking) => (
          <BookingDetail
            key={booking.id}
            booking={booking}
            setClientId={setClientId}
            setShowClientInfoPanel={setShowClientInfoPanel}
            setBooking={setBooking}
            handleClick={handleClick}
          />
        ))}
      </Box>
    </Box>
  );
};

export default PastBookings;
